import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import StatCards from "./StatCards";
import FrameworkList from "./FrameworkList";
import FrameworkDetail from "./FrameworkDetail";
import ControlsTable from "./ControlsTable";
import NotificationsPanel from "./NotificationsPanel";
import { useFrameworks, useControls, usePatchControl } from "./useComplianceData";
import { getCompanyUsers } from "../../../services/axios/AuthApis/TeamCollabApis";

const COMPLIANCE_TABS = ["all", "implemented", "pending", "assigned", "unassigned"];
const TRUST_CENTER_TABS = ["dashboard", "frameworks", "controls", "framework-master"];

/**
 * Compliance Overview - drill-down replacement for the old static
 * Enhanced/Dashboard KPI block. State (activeCard/frameworkId/tab/search/
 * category/page/flag) is mirrored into the URL using compliance-specific
 * query parameters so it does not conflict with Trust Center navigation.
 */
export default function ComplianceOverview({ customerId, customerMasterId }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeCard, setActiveCard] = useState(searchParams.get("card") || "enabled");
  const [selectedFrameworkId, setSelectedFrameworkId] = useState(searchParams.get("fw") || null);
  const legacyTab = searchParams.get("tab");
  const [tab, setTab] = useState(
    searchParams.get("complianceTab") ||
      (COMPLIANCE_TABS.includes(legacyTab) ? legacyTab : "all"),
  );
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [frameworkFilter, setFrameworkFilter] = useState("");
  const [flag, setFlag] = useState("");
  const [page, setPage] = useState(parseInt(searchParams.get("page") || "1", 10));
  const [pageSize, setPageSize] = useState(12);
  const [owners, setOwners] = useState([]);

  const { frameworks, loading: frameworksLoading, error: frameworksError, reload: reloadFrameworks } = useFrameworks(customerId);

  useEffect(() => {
    setSearchParams((current) => {
      const params = new URLSearchParams(current);
      if (!TRUST_CENTER_TABS.includes(params.get("tab"))) {
        params.set("tab", "dashboard");
      }
      if (activeCard) params.set("card", activeCard);
      else params.delete("card");
      if (selectedFrameworkId) params.set("fw", selectedFrameworkId);
      else params.delete("fw");
      if (tab !== "all") params.set("complianceTab", tab);
      else params.delete("complianceTab");
      if (page > 1) params.set("page", String(page));
      else params.delete("page");
      return params;
    }, { replace: true });
  }, [activeCard, selectedFrameworkId, tab, page, setSearchParams]);

  useEffect(() => {
    if (!customerMasterId) return;
    getCompanyUsers(customerMasterId, 1, 200).then((res) => {
      const list = (res?.results || []).map((u) => ({
        id: u.user_id,
        name: `${u.firstname || ""} ${u.lastname || ""}`.trim() || u.email,
      }));
      setOwners(list);
    }).catch(() => setOwners([]));
  }, [customerMasterId]);

  const stats = useMemo(() => {
    const enabledFrameworks = frameworks.filter((f) => f.enabled);
    const totalImplemented = enabledFrameworks.reduce((s, f) => s + f.implemented, 0);
    const totalAll = enabledFrameworks.reduce((s, f) => s + f.totalControls, 0);
    return {
      enabledCount: enabledFrameworks.length,
      totalCount: frameworks.length,
      completed: totalImplemented,
      pending: totalAll - totalImplemented,
      score: totalAll ? Math.round((totalImplemented / totalAll) * 100) : 0,
      overdue: enabledFrameworks.reduce((s, f) => s + (f.overdue || 0), 0),
      dueSoon: enabledFrameworks.reduce((s, f) => s + (f.dueSoon || 0), 0),
      unassigned: enabledFrameworks.reduce((s, f) => s + (f.unassigned || 0), 0),
    };
  }, [frameworks]);

  const selectCard = (card) => {
    setActiveCard(card);
    setSelectedFrameworkId(null);
    // The card only sets the DEFAULT tab (per spec) - "All"/"Assigned"/
    // "Unassigned" inside this view must still show the full control set,
    // not stay silently re-scoped to just implemented/pending.
    setTab(card === "completed" ? "implemented" : card === "pending" ? "pending" : "all");
    setSearch("");
    setCategory("");
    setFrameworkFilter("");
    setFlag("");
    setPage(1);
  };

  const handleNotificationClick = ({ card, flag: f, tab: t, frameworkId }) => {
    setActiveCard(card);
    setSelectedFrameworkId(frameworkId || null);
    setTab(t || "all");
    setFlag(f || "");
    setSearch("");
    setCategory("");
    setPage(1);
  };

  const selectedFramework = frameworks.find((f) => f.id === selectedFrameworkId);

  const listFrameworks = activeCard === "enabled"
    ? frameworks.filter((f) => f.enabled)
    : frameworks;

  const showGlobalControls = activeCard === "completed" || activeCard === "pending";

  // Tab alone decides the status/owner scope here - "All"/"Assigned"/
  // "Unassigned" intentionally span every control, not just the card's
  // implemented/pending subset (the card only picked the default tab).
  const globalFilters = useMemo(() => ({
    framework: frameworkFilter,
    status: tab === "pending" ? "pending" : tab === "implemented" ? "implemented" : "",
    owner: tab === "unassigned" ? "unassigned" : tab === "assigned" ? "assigned" : "",
    category,
    search,
    flag,
    page,
    pageSize,
  }), [frameworkFilter, tab, category, search, flag, page, pageSize]);

  const globalControls = useControls(showGlobalControls ? customerId : null, globalFilters);
  const displayedGlobalControls = globalControls.results;
  const { patch } = usePatchControl(customerId);

  const onPatch = async (controlId, body) => {
    try {
      await patch(controlId, body);
      await globalControls.reload();
      await reloadFrameworks();
      return true;
    } catch (e) {
      return false;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row gap-4 items-stretch">
        <div className="w-full max-w-sm h-[380px] flex-shrink-0">
          <NotificationsPanel
            frameworks={frameworks}
            overdueCount={stats.overdue}
            dueSoonCount={stats.dueSoon}
            unassignedCount={stats.unassigned}
            onNotificationClick={handleNotificationClick}
          />
        </div>
        <div className="flex-1">
          <StatCards stats={stats} activeCard={activeCard} onSelect={selectCard} />
        </div>
      </div>

      {frameworksError && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-4">{frameworksError}</div>
      )}

      {!customerId && (
        <div className="bg-amber-50 border border-amber-200 text-amber-700 text-sm rounded-lg p-4">
          No customer is selected for this view - Compliance Overview needs a customer_admin account, or (for
          security_analyst/auditor) a company picked from the company switcher.
        </div>
      )}

      {!selectedFramework && (activeCard === "enabled" || activeCard === "total" || activeCard === "score") && (
        frameworksLoading ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-400">Loading frameworks…</div>
        ) : (
          <FrameworkList frameworks={listFrameworks} onOpen={(id) => { setSelectedFrameworkId(id); setPage(1); }} />
        )
      )}

      {selectedFramework && (
        <FrameworkDetail
          customerId={customerId}
          framework={selectedFramework}
          owners={owners}
          onBack={() => setSelectedFrameworkId(null)}
          state={{ tab, search, flag, page, category }}
          setState={(updater) => {
            const next = typeof updater === "function" ? updater({ tab, search, flag, page, category }) : updater;
            if (next.tab !== undefined) setTab(next.tab);
            if (next.search !== undefined) setSearch(next.search);
            if (next.flag !== undefined) setFlag(next.flag);
            if (next.page !== undefined) setPage(next.page);
            if (next.category !== undefined) setCategory(next.category);
          }}
        />
      )}

      {showGlobalControls && (
        <ControlsTable
          controls={displayedGlobalControls}
          loading={globalControls.loading}
          error={globalControls.error}
          count={globalControls.count}
          page={page}
          pageSize={pageSize}
          totalPages={globalControls.total_pages}
          tabCounts={globalControls.tab_counts}
          tab={tab}
          onTabChange={(t) => { setTab(t); setPage(1); }}
          search={search}
          onSearchChange={(s) => { setSearch(s); setPage(1); }}
          category={category}
          onCategoryChange={(c) => { setCategory(c); setPage(1); }}
          categories={[...new Set(globalControls.results.map((c) => c.category))]}
          framework={frameworkFilter}
          onFrameworkChange={(f) => { setFrameworkFilter(f); setPage(1); }}
          frameworks={frameworks}
          showFrameworkFilter
          flag={flag}
          onClearFlag={() => { setFlag(""); setPage(1); }}
          owners={owners}
          onPageChange={(nextPage, nextSize) => {
            if (nextSize && nextSize !== pageSize) {
              setPageSize(nextSize);
              setPage(1);
            } else {
              setPage(nextPage);
            }
          }}
          onPatch={onPatch}
          onClearFilters={() => { setTab("all"); setSearch(""); setCategory(""); setFrameworkFilter(""); setFlag(""); setPage(1); }}
        />
      )}
    </div>
  );
}
